import { StripeElementLocale } from '@stripe/stripe-js';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { TypePaymentOption } from '../../../../../../@types/generated';
import { microcopySet, useMicrocopy } from '../../../../../utils/contentfulMicrocopy';
import { scrollToDonationBox } from '../../../../../utils/helpers/scroll';
import useLocale from '../../../../../utils/hooks/useLocale';
import { DONATION_BOX_ID } from '../../../payment/constants';
import FixedMobileButton from '../../../payment/FixedMobileButton';
import { StripePaymentCategory } from '../../../payment/PaymentCategory';
import StripeCheckoutWithSelector from '../../../payment/StripeCheckoutWithSelector';

function StripeInlineWidget({
  category,
  campaign,
  paymentOption,
  thankYouPageSlugOverride,
  colorTheme,
  variant,
  noOuterBorder,
  disableFixedMobileButton,
  titleSection,
}: {
  category?: StripePaymentCategory;
  campaign?: string;
  paymentOption?: TypePaymentOption;
  thankYouPageSlugOverride?: string;
  variant?: 'with-borders' | 'without-borders';
  colorTheme?: 'default' | 'dark' | 'ppc';
  noOuterBorder?: boolean;
  disableFixedMobileButton?: boolean;
  titleSection?: ReactNode;
}) {
  const router = useRouter();
  const locale = useLocale();
  const t = useMicrocopy();
  const [showDonationButton, setShowDonationButton] = useState(false);
  const [isClientSideRender, setIsClientSideRender] = useState(false);

  const onSuccess = ({
    paymentRequestId,
    subscriptionId,
  }: {
    paymentRequestId?: string;
    subscriptionId?: string;
  }) => {
    const id = subscriptionId || paymentRequestId;
    const type = paymentRequestId ? 'one-off' : 'subscription';
    const count = 1;
    if (thankYouPageSlugOverride) {
      router.push(
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}&slug=${thankYouPageSlugOverride}`,
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}&slug=${thankYouPageSlugOverride}`,
        { locale },
      );
    } else {
      router.push(
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}`,
        `/thank-you?type=${type}&id=${id}&count=${count}&campaign=${campaign}`,
        { locale },
      );
    }
  };

  const handleScroll = useCallback(() => {
    const donationBox = document.getElementById(DONATION_BOX_ID);
    if (donationBox) {
      const donationBoxTopPos = donationBox.getBoundingClientRect().bottom + window.scrollY;
      if (window.scrollY > donationBoxTopPos) {
        setShowDonationButton(true);
      } else {
        setShowDonationButton(false);
      }
    }
  }, [setShowDonationButton]);

  useEffect(() => {
    const debouncedHandleScroll = debounce(handleScroll, 300, {
      leading: true,
      trailing: true,
      maxWait: 100,
    });
    window.addEventListener('scroll', debouncedHandleScroll);
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsClientSideRender(true);
  }, [setIsClientSideRender]);

  return (
    <>
      <StripeCheckoutWithSelector
        id={DONATION_BOX_ID}
        category={category}
        onSuccess={onSuccess}
        campaign={campaign}
        paymentOption={paymentOption?.fields}
        locale={locale as StripeElementLocale}
        variant={variant}
        colorTheme={colorTheme}
        noOuterBorder={noOuterBorder}
        titleSection={titleSection}
      />
      {!disableFixedMobileButton && isClientSideRender && (
        <FixedMobileButton
          show={showDonationButton}
          onDonate={scrollToDonationBox}
          donateText={t(microcopySet.PAYMENT_OPTION, 'donate')}
        />
      )}
    </>
  );
}

export default StripeInlineWidget;
